import * as React from 'react';
import { createPortal } from 'react-dom';
import apiService from '../../services/api';
import { PastNegotiationSourcePdfPanel } from './PastNegotiationSourcePdfPanel';

export interface PastNegotiationSourceModalProps {
  /** Shown in the modal header (replaces a separate filename row in the body). */
  filename: string;
  filenameDir?: 'rtl' | 'ltr';
  projectId: string;
  playbookId: string;
  /** Plain text shown on the card — used to locate the highlight in the PDF. */
  searchText?: string;
  domain?: 'construction-loan' | 'real-estate';
  onClose: () => void;
}

/**
 * Full-screen PDF source preview — same UI as Past negotiations “Source” on clause cards.
 */
export const PastNegotiationSourceModal: React.FC<PastNegotiationSourceModalProps> = ({
  filename,
  filenameDir = 'ltr',
  projectId,
  playbookId,
  searchText,
  domain = 'construction-loan',
  onClose,
}) => {
  const [pdfState, setPdfState] = React.useState<{
    loading: boolean;
    error: string | null;
    buffer: ArrayBuffer | null;
    page: number;
    total: number;
  } | null>({
    loading: true,
    error: null,
    buffer: null,
    page: 1,
    total: 1,
  });

  React.useEffect(() => {
    let cancelled = false;
    setPdfState({
      loading: true,
      error: null,
      buffer: null,
      page: 1,
      total: 1,
    });
    void (async () => {
      try {
        const { blob, highlightPage, totalPages } = await apiService.getPastNegotiationSourcePdf(
          projectId,
          playbookId,
          domain,
          searchText,
        );
        const buffer = await blob.arrayBuffer();
        if (cancelled) return;
        setPdfState({
          loading: false,
          error: null,
          buffer,
          page: highlightPage,
          total: totalPages,
        });
      } catch (e: unknown) {
        if (!cancelled) {
          setPdfState({
            loading: false,
            error: e instanceof Error ? e.message : 'Failed to load source PDF',
            buffer: null,
            page: 1,
            total: 1,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, playbookId, domain, searchText]);

  const modal = (
    <div
      className="cc-modal-overlay cc-modal-overlay--past-source"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="cc-modal cc-modal--past-source"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="past-negotiation-source-title"
      >
        <div className="cc-modal-header cc-modal-header--past-source">
          <span
            className="cc-modal-title cc-modal-title--past-source-filename"
            id="past-negotiation-source-title"
            dir={filenameDir}
            title={filename}
          >
            {filename}
          </span>
          <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="cc-modal-body cc-modal-body--past-source">
          {pdfState?.loading && (
            <div className="cc-source-loading" role="status" aria-live="polite">
              <span className="cc-source-loading-spinner" aria-hidden />
              <p className="cc-source-loading-text">Loading the source…</p>
            </div>
          )}
          {pdfState?.error && <p className="cc-source-pdf-error">{pdfState.error}</p>}
          {pdfState?.buffer && !pdfState.loading && !pdfState.error && (
            <PastNegotiationSourcePdfPanel
              pdfData={pdfState.buffer}
              initialPage={pdfState.page}
              totalPagesHint={pdfState.total}
            />
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
};
